import { useId, useMemo, useState } from "react";
import "./ActionCenter.css";

const TYPE_LABELS = {
  download: "원본 보관",
  original_download: "원본 보관",
  theme: "촬영 준비",
  theme_selection: "촬영 준비",
  photo: "제작 대기",
  photo_selection: "제작 대기",
  product: "제작 대기",
  ORIGINAL_DOWNLOAD: "원본 보관",
  THEME_SELECTION: "촬영 준비",
  PRODUCT_PHOTO_SELECTION: "제작 대기",
};

const URGENCY_RANK = {
  critical: 0,
  urgent: 0,
  high: 1,
  medium: 2,
  normal: 3,
  low: 4,
};

const ICON_PATHS = {
  download: (
    <>
      <path d="M12 3v11" />
      <path d="m7.5 10 4.5 4.5 4.5-4.5" />
      <path d="M5 20h14" />
    </>
  ),
  theme: (
    <>
      <path d="M12 3 9.8 8.2 4 10.5l5.8 2.3L12 18l2.2-5.2 5.8-2.3-5.8-2.3L12 3Z" />
      <path d="m5.5 3 .8 1.9 2 .8-2 .8-.8 1.9-.8-1.9-2-.8 2-.8L5.5 3Z" />
    </>
  ),
  photo: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.7" />
      <path d="m5.5 17 4.2-4 3.1 2.7 2.2-2 3.5 3.3" />
    </>
  ),
};

function getIconType(type = "") {
  const normalizedType = String(type).toLowerCase();
  if (normalizedType.includes("download")) return "download";
  if (normalizedType.includes("theme")) return "theme";
  return "photo";
}

function ActionIcon({ type }) {
  const iconType = getIconType(type);

  return (
    <span className={`action-center__icon action-center__icon--${iconType}`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[iconType]}
      </svg>
    </span>
  );
}

function getDueTimestamp(item) {
  const value = item.expiresAt || item.dueAt;
  const timestamp = value ? new Date(value).getTime() : Number.POSITIVE_INFINITY;
  return Number.isNaN(timestamp) ? Number.POSITIVE_INFINITY : timestamp;
}

export function ActionCenterHeader({ count, id, standalone = false }) {
  const className = standalone
    ? "action-center action-center__header action-center__header--standalone"
    : "action-center__header";

  return (
    <header className={className}>
      <div>
        <span className="action-center__eyebrow">TODAY</span>
        <h2 id={id}>지금 해야 할 일</h2>
      </div>
      <span className="action-center__count" aria-label={`해야 할 일 ${count}개`}>
        {count}
      </span>
    </header>
  );
}

export function sortActionItems(items = []) {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftRank = Number.isFinite(left.item.priorityRank)
        ? left.item.priorityRank
        : URGENCY_RANK[left.item.urgency || left.item.priority] ?? URGENCY_RANK.normal;
      const rightRank = Number.isFinite(right.item.priorityRank)
        ? right.item.priorityRank
        : URGENCY_RANK[right.item.urgency || right.item.priority] ?? URGENCY_RANK.normal;

      if (leftRank !== rightRank) return leftRank - rightRank;

      const dueDifference = getDueTimestamp(left.item) - getDueTimestamp(right.item);
      return dueDifference || left.index - right.index;
    })
    .map(({ item }) => item);
}

function getDdayLabel(item) {
  if (typeof item.ddayLabel === "string") return item.ddayLabel;
  if (Number.isFinite(item.remainingDays)) {
    if (item.remainingDays < 0) return "기한 지남";
    if (item.remainingDays === 0) return "D-DAY";
    return `D-${item.remainingDays}`;
  }
  if (typeof item.dDay === "string") return item.dDay;
  if (typeof item.dDay !== "number") return "";
  if (item.dDay === 0) return "D-DAY";
  return item.dDay > 0 ? `D-${item.dDay}` : `D+${Math.abs(item.dDay)}`;
}

function formatKoreanDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("ko-KR", {
    month: "long",
    day: "numeric",
  }).format(date);
}

function getDeadlineLabel(item) {
  if (item.deadlineLabel) return item.deadlineLabel;
  if (item.expiresAt) {
    const date = formatKoreanDate(item.expiresAt);
    return date ? `${date} 보관 종료` : "";
  }
  if (item.dueAt) {
    const date = formatKoreanDate(item.dueAt);
    return date ? `${date}까지` : "";
  }
  return "";
}

function getProgress(item) {
  const current = Number(item.progress?.current ?? item.progressCurrent);
  const total = Number(item.progress?.total ?? item.progress?.required ?? item.progressTotal);
  if (!Number.isFinite(current) || !Number.isFinite(total) || total <= 0) return null;

  const safeCurrent = Math.max(0, Math.min(current, total));
  const unit = item.progress?.unit || "개";
  return {
    current: safeCurrent,
    total,
    label: item.progress?.label || item.progressLabel || `${total}${unit} 중 ${safeCurrent}${unit} 완료`,
  };
}

function TaskBadges({ item, primary = false }) {
  const dDay = getDdayLabel(item);
  const urgency = item.urgency || item.priority;
  const isUrgent = urgency === "critical" || urgency === "urgent";

  return (
    <div className="action-center__badges" aria-label="할 일 상태">
      <span className="action-center__type-badge">
        {item.categoryLabel || TYPE_LABELS[item.type] || "안내"}
      </span>
      {isUrgent && (
        <span className="action-center__urgency-badge">
          <span aria-hidden="true">!</span> 긴급
        </span>
      )}
      {dDay && (
        <span className={`action-center__dday${primary && isUrgent ? " action-center__dday--urgent" : ""}`}>
          {dDay}
        </span>
      )}
    </div>
  );
}

function TaskMeta({ item }) {
  const values = [item.fileSizeLabel || item.fileSize, getDeadlineLabel(item)].filter(Boolean);

  if (values.length === 0) return null;

  return (
    <p className="action-center__meta">
      {values.map((value, index) => (
        <span key={`${value}-${index}`}>
          {index > 0 && <span className="action-center__meta-divider" aria-hidden="true">·</span>}
          {value}
        </span>
      ))}
    </p>
  );
}

function TaskProgress({ item }) {
  const progress = getProgress(item);
  if (!progress) return null;

  return (
    <div className="action-center__progress">
      <div className="action-center__progress-copy">
        <span>{progress.label}</span>
        <strong>{Math.round((progress.current / progress.total) * 100)}%</strong>
      </div>
      <progress value={progress.current} max={progress.total} aria-label={progress.label} />
    </div>
  );
}

function ActionButton({ item, onAction, compact = false }) {
  const label = item.actionLabel || item.cta?.label || "확인하기";
  const href = item.href || item.cta?.path;
  const className = `action-center__action${compact ? " action-center__action--compact" : ""}`;

  if (href && !onAction) {
    return (
      <a className={className} href={href} aria-label={`${item.title}: ${label}`}>
        {label}
        <span aria-hidden="true">→</span>
      </a>
    );
  }

  return (
    <button
      className={className}
      type="button"
      onClick={() => onAction?.(item)}
      disabled={item.actionDisabled}
      aria-label={`${item.title}: ${label}`}
    >
      {label}
      <span aria-hidden="true">→</span>
    </button>
  );
}

function SnoozeButton({ item, onSnooze, compact = false }) {
  if (item.canSnooze === false || !onSnooze) return null;

  const isSnoozed = Boolean(item.snoozed || item.snoozedUntil);
  const label = isSnoozed ? "내일 다시 알려드릴게요" : item.snoozeLabel || "내일 다시 알림";

  return (
    <button
      className={`action-center__snooze${compact ? " action-center__snooze--compact" : ""}`}
      type="button"
      onClick={() => onSnooze(item)}
      disabled={isSnoozed || item.snoozeDisabled}
      aria-label={`${item.title}: ${label}`}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9Z" />
        <path d="M9.8 21h4.4" />
      </svg>
      {label}
    </button>
  );
}

function PrimaryTask({ item, onAction, onSnooze }) {
  const urgency = item.urgency || item.priority || "normal";
  return (
    <article className={`action-center__primary action-center__primary--${urgency}`}>
      <div className="action-center__primary-heading">
        <ActionIcon type={item.type} />
        <TaskBadges item={item} primary />
      </div>

      <div className="action-center__primary-copy">
        <h3>{item.title}</h3>
        {item.description && <p className="action-center__description">{item.description}</p>}
        <TaskMeta item={item} />
        <TaskProgress item={item} />
      </div>

      <div className="action-center__primary-actions">
        <ActionButton item={item} onAction={onAction} />
        <SnoozeButton item={item} onSnooze={onSnooze} />
      </div>
    </article>
  );
}

function CompactTask({ item, onAction, onSnooze }) {
  return (
    <li>
      <article className="action-center__compact">
        <ActionIcon type={item.type} />
        <div className="action-center__compact-copy">
          <TaskBadges item={item} />
          <h3>{item.title}</h3>
          {item.description && <p className="action-center__description">{item.description}</p>}
          <TaskMeta item={item} />
          <TaskProgress item={item} />
          <SnoozeButton item={item} onSnooze={onSnooze} compact />
        </div>
        <ActionButton item={item} onAction={onAction} compact />
      </article>
    </li>
  );
}

/**
 * 홈의 행동 유도 영역.
 *
 * item: {
 *   id, type, title, description, urgency|priority, dDay|ddayLabel,
 *   dueAt, expiresAt, fileSizeLabel,
 *   progress: { current, total|required, unit, label },
 *   actionLabel|cta: { label, path }, href, canSnooze, snoozed
 * }
 */
export default function ActionCenter({
  items = [],
  onAction,
  onSnooze,
  defaultExpanded = true,
  className = "",
  showHeader = true,
  labelledBy,
}) {
  const titleId = `action-center-${useId().replace(/:/g, "")}`;
  const listId = `${titleId}-list`;
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const sortedItems = useMemo(() => sortActionItems(items), [items]);

  if (sortedItems.length === 0) return null;

  const [primaryItem, ...compactItems] = sortedItems;

  return (
    <section
      className={`action-center ${className}`.trim()}
      aria-labelledby={showHeader ? titleId : labelledBy}
      aria-label={!showHeader && !labelledBy ? "지금 해야 할 일" : undefined}
    >
      {showHeader ? <ActionCenterHeader count={sortedItems.length} id={titleId} /> : null}

      <PrimaryTask item={primaryItem} onAction={onAction} onSnooze={onSnooze} />

      {compactItems.length > 0 && (
        <div className="action-center__more">
          <button
            className="action-center__toggle"
            type="button"
            aria-expanded={isExpanded}
            aria-controls={listId}
            onClick={() => setIsExpanded((value) => !value)}
          >
            <span>{isExpanded ? "나머지 할 일 접기" : `할 일 ${compactItems.length}개 더보기`}</span>
            <svg
              className={isExpanded ? "action-center__toggle-icon action-center__toggle-icon--open" : "action-center__toggle-icon"}
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
            >
              <path d="m5 7.5 5 5 5-5" />
            </svg>
          </button>

          <ul id={listId} className="action-center__list" hidden={!isExpanded}>
            {compactItems.map((item, index) => (
              <CompactTask
                item={item}
                key={item.id ?? `${item.type}-${index}`}
                onAction={onAction}
                onSnooze={onSnooze}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
